
import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Copy, Trash2, Eye, Plus, Mail } from "lucide-react";
import { EVENT_TYPES } from "@/types/notifications";
import { getTone } from '../../theme/tones';

const getEventLabel = (eventType, daysOffset) => {
  switch(eventType) {
    case EVENT_TYPES.REGISTRATION:
      return "À l'inscription";
    case EVENT_TYPES.BEFORE_TRAINING:
      return `${daysOffset} jour${daysOffset > 1 ? 's' : ''} avant la formation`;
    case EVENT_TYPES.TRAINING_START:
      return "Au début de la formation";
    case EVENT_TYPES.TRAINING_END:
      return "À la fin de la formation";
    case EVENT_TYPES.AFTER_TRAINING:
      return `${daysOffset} jour${daysOffset > 1 ? 's' : ''} après la formation`;
    default:
      return "Manuel uniquement";
  }
};

const EmailTemplateList = ({ templates, onDelete, onDuplicate, onToggleActive }) => {
  const navigate = useNavigate();
  
  const handleCreateTemplate = () => {
    console.log('EmailTemplateList - handleCreateTemplate clicked');
    navigate('/dashboard/email-notifications/new');
  };

  return (
    <div>
      {templates.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/70 shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-5">
            <Mail className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-lg font-black text-slate-900 mb-1.5">Aucun modèle de notification</h2>
          <p className="text-sm text-slate-500 font-medium mb-6">Créez votre premier modèle pour automatiser vos envois.</p>
          <Button
            onClick={handleCreateTemplate}
            className="rounded-xl font-bold bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25 transition-all hover:-translate-y-0.5"
          >
            <Plus className="mr-2 h-4 w-4" /> Créer un premier modèle
          </Button>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2.5">
              <span className={`inline-block w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_${getTone('blue').base}]`} />
              Modèles de notifications
            </h2>
            <Button
              onClick={handleCreateTemplate}
              className="rounded-xl font-bold bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25 transition-all hover:-translate-y-0.5"
            >
              <Plus className="mr-2 h-4 w-4" /> Créer un modèle
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70 hover:bg-slate-50/70 border-slate-200/80">
                  <TableHead className="w-[250px] text-xs font-bold uppercase tracking-wider text-slate-500">Nom</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Déclencheur</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Sujet</TableHead>
                  <TableHead className="w-[100px] text-xs font-bold uppercase tracking-wider text-slate-500">Statut</TableHead>
                  <TableHead className="text-right w-[170px] text-xs font-bold uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} className="border-slate-100 transition-colors hover:bg-blue-50/40">
                    <TableCell className="font-bold text-slate-800">{template.name}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {template.event_type ? getEventLabel(template.event_type, template.days_offset) : "Manuel uniquement"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-slate-500">{template.subject}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`cursor-pointer rounded-lg border font-bold transition-colors ${
                          template.is_active
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100"
                            : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"
                        }`}
                        onClick={() => onToggleActive(template.id)}
                      >
                        <span className={`mr-1.5 inline-block w-1.5 h-1.5 rounded-full ${template.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {template.is_active ? "Actif" : "Inactif"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="ghost" size="icon" className="rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600" onClick={() => navigate(`/dashboard/email-notifications/${template.id}/preview`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600" onClick={() => navigate(`/dashboard/email-notifications/${template.id}/edit`)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-lg text-slate-500 hover:bg-indigo-50 hover:text-indigo-600" onClick={() => onDuplicate(template.id)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => onDelete(template.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailTemplateList;
